import { useNavigate } from "react-router-dom";
import {
  Cpu, ArrowRight, TrendingDown, Smartphone, EyeOff, HandMetal,
  AlertTriangle, Search, Target, Play, TrendingUp, Scan, Box, Radio,
  Building2, Building, Network, Check, BarChart3, Package, Wifi,
} from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";
import { Button } from "../components/ui/button";

// --- Reusable atoms --------------------------------------------------------
const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold tracking-[0.22em] text-[#025979] uppercase mb-3">
    {children}
  </p>
);

const H2 = ({ children, className = "" }) => (
  <h2
    className={`text-3xl sm:text-4xl lg:text-[42px] font-bold tracking-tight text-[#111827] leading-[1.1] ${className}`}
    style={{ fontFamily: "Chivo, sans-serif" }}
  >
    {children}
  </h2>
);

const Lead = ({ children }) => (
  <p className="mt-4 text-base sm:text-lg text-[#4B5563] leading-relaxed">{children}</p>
);

// --- Mini mockup visuals (one per platform block) --------------------------
// These are CSS/SVG enterprise visuals — crisp, on-brand, and don't rely on
// external stock imagery. Kept flat and calm so the copy stays the hero.

const SmartStrategyVisual = () => (
  <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-md overflow-hidden">
    <img
      src="/images/image-21.webp"
      alt="SmartStrategy: Input Assessment → Evaluation & Decision Engine → Transformation Output"
      width="1600"
      height="1600"
      loading="lazy"
      className="w-full h-auto block"
    />
  </div>
);

const SmartExecutionVisual = () => (
  <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-md overflow-hidden">
    <img
      src="/images/smartexectution.webp"
      alt="SmartExecution: RF device, SmartCenter dashboard, and SmartBot AI assistant in a live warehouse"
      width="1600"
      height="1600"
      loading="lazy"
      className="w-full h-auto block"
    />
  </div>
);

const SmartAccelerationVisual = () => (
  <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-md overflow-hidden">
    <img
      src="/images/smartaccelaration.webp"
      alt="SmartAcceleration: Implementation Accelerator dashboard with Discover → Design → Build & Deploy → Go-Live phases and 40–50% faster implementation"
      width="1600"
      height="1600"
      loading="lazy"
      className="w-full h-auto block"
    />
  </div>
);

const SmartTechVisual = () => (
  <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-md overflow-hidden">
    <img
      src="/images/smartwarehouse-tech.webp"
      alt="SmartWarehouse Tech: warehouse operator with voice headset, RFID tracking, autonomous robot, robotic arm on conveyor, and drone scanning"
      width="1600"
      height="1600"
      loading="lazy"
      className="w-full h-auto block"
    />
  </div>
);

const SmartIntegrationsVisual = () => (
  <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-md overflow-hidden">
    <img
      src="/images/smartintegrations.webp"
      alt="SmartIntegrations: connected ecosystem with inspection tablet, analytics dashboard, freight management, and process flow across warehouse, trucks, and cloud"
      width="1600"
      height="1600"
      loading="lazy"
      className="w-full h-auto block"
    />
  </div>
);

const CoreValueVisual = () => (
  <div className="rounded-sm shadow-lg overflow-hidden border border-[#E5E7EB] bg-white">
    <div className="overflow-hidden">
      <img
        src="/images/warehousex-what-you-get.webp"
        alt="WarehouseX deliverables on a desk: Transformation Report dashboard on laptop, KPI Summary on tablet, printed Warehouse Transformation Report and Transformation Roadmap"
        width="1600"
        height="1600"
        loading="lazy"
        className="w-full h-auto block transform scale-[1.06] origin-center"
      />
    </div>
  </div>
);

// --- Split block (alternating) ---------------------------------------------
function PlatformBlock({
  idx, reverse, title, eyebrow, desc, link, linkLabel, visual,
  includes, bullets, positioning, anchorId,
}) {
  return (
    <div
      id={anchorId}
      data-testid={`platform-block-${idx}`}
      className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center scroll-mt-24`}
    >
      <div className={`order-2 lg:col-span-5 ${reverse ? "lg:order-2" : "lg:order-1"}`}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h3
          className="text-2xl sm:text-3xl font-bold text-[#111827] tracking-tight leading-tight"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {title}
        </h3>
        <p className="mt-6 text-base text-[#4B5563] leading-relaxed">{desc}</p>
        {includes && (
          <p className="mt-5 text-xs text-[#6B7280]">
            <span className="font-semibold text-[#111827]">Includes:</span>{" "}
            <span className="text-[#374151]">{includes}</span>
          </p>
        )}
        {bullets && bullets.length > 0 && (
          <ul className="mt-7 space-y-3">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm text-[#111827]">
                <Check className="w-4 h-4 text-[#025979] mt-0.5 shrink-0" strokeWidth={2.5} />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        )}
        {positioning && (
          <p className="mt-7 text-sm italic text-[#6B7280]">{positioning}</p>
        )}
        {link && (
          <a
            href={link}
            data-testid={`platform-block-${idx}-link`}
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#025979] hover:text-[#03738C] group"
          >
            {linkLabel}
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </a>
        )}
      </div>
      <div className={`order-1 lg:col-span-7 ${reverse ? "lg:order-1" : "lg:order-2"}`}>
        {visual}
      </div>
    </div>
  );
}

// --- Section data ----------------------------------------------------------
const PROBLEMS = [
  { icon: TrendingDown, title: "Low productivity" },
  { icon: Smartphone, title: "Poor RF user experience" },
  { icon: EyeOff, title: "Limited real-time visibility" },
  { icon: HandMetal, title: "Manual operations" },
  { icon: AlertTriangle, title: "Frequent operational issues" },
];

const APPROACH = [
  { icon: Search, title: "Evaluate", desc: "Assess your current warehouse operations, systems, and performance baselines." },
  { icon: Target, title: "Improve", desc: "Identify gaps, define improvements, and build a clear transformation roadmap." },
  { icon: Play, title: "Execute", desc: "Deploy solutions using proven accelerators, automation, and intelligent tools." },
  { icon: TrendingUp, title: "Optimize", desc: "Continuously enhance performance using real-time insights and operational intelligence." },
];

const PLATFORM_BLOCKS = [
  {
    anchorId: "smartstrategy",
    eyebrow: "EVALUATE & IMPROVE", title: "SmartStrategy",
    desc: "Define the right warehouse strategy through structured evaluation, gap analysis, and a clear ROI-driven transformation roadmap.",
    includes: "SmartEvaluate • SmartImprove • SmartBlueprint",
    bullets: [
      "Identify inefficiencies across operations",
      "Data-driven decisions, not assumptions",
      "Clear ROI roadmap before investing",
    ],
    positioning: "Your first step into warehouse transformation.",
    link: "/warehousex/smartstrategy", linkLabel: "Start Your Warehouse Strategy",
    visual: <SmartStrategyVisual />,
  },
  {
    anchorId: "smartexecution",
    eyebrow: "EXECUTION & UX", title: "SmartExecution",
    desc: "Execute warehouse operations with modern RF interfaces, real-time visibility, and intelligent automation — improving speed, accuracy, and operator efficiency.",
    includes: "SmartRF (PreBilt) • SmartEWM Bot • Command Center",
    bullets: [
      "Improve operator productivity and task flow",
      "Reduce errors with guided execution",
      "Enable real-time visibility across operations",
      "Minimize training with intuitive UX",
    ],
    link: "/smartexecution", linkLabel: "Explore SmartExecution",
    visual: <SmartExecutionVisual />,
  },
  {
    anchorId: "smartacceleration",
    eyebrow: "Delivery Accelerators", title: "SmartAcceleration",
    desc: "Accelerate implementation using prebuilt kits, industry-specific solutions, and reusable components — reducing time, risk, and delivery effort.",
    includes: "Rapid Go-Live Kits • Industry Packs • Solution Accelerators",
    bullets: [
      "Faster go-live using proven rollout kits",
      "Industry-specific templates and best practices",
      "Reusable components for custom developments and enhancements",
      "Scalable delivery across multiple warehouses",
    ],
    link: "/smartacceleration", linkLabel: "Accelerate Your Warehouse Rollout",
    visual: <SmartAccelerationVisual />,
  },
  {
    anchorId: "smartwarehouse-tech",
    eyebrow: "Next-Gen Capabilities", title: "SmartWarehouse Tech",
    desc: "Enable next-gen warehouse capabilities including RFID, automation, and real-time tracking — integrated natively into SAP EWM and TM processes.",
    includes: "RFID Enablement • Voice Picking • Automation • Drone Inventory",
    bullets: [
      "Real-time visibility with RFID-enabled tracking",
      "Faster, hands-free execution using voice workflows",
      "Automated operations with conveyors and robotics",
      "High-speed, accurate inventory tracking using drones",
    ],
    link: "/smartwarehouse-tech", linkLabel: "Explore Future Warehouse Technology",
    visual: <SmartTechVisual />,
  },
  {
    anchorId: "smartintegrations",
    eyebrow: "Extensions & Integrations", title: "SmartIntegrations",
    desc: "Extend SAP EWM into a connected ecosystem with intelligent inspection, automated shipping, and data-driven process insights — seamlessly integrated into your warehouse operations.",
    includes: "SmartInspect • Tcube • Futuroot",
    bullets: [
      "End-to-end visibility across inspection, shipping, and warehouse operations",
      "Intelligent freight management and automated shipping workflows",
      "Real-time operational insights through process mining",
      "Integrated inspection and quality control within warehouse processes",
    ],
    link: "/smartintegrations", linkLabel: "Explore SmartIntegrations",
    visual: <SmartIntegrationsVisual />,
  },
];

const SYSTEM_PILLARS = [
  {
    title: "SmartStrategy",
    desc: "Define the right warehouse roadmap with evaluation, gap analysis, and ROI clarity.",
    href: "/warehousex/smartstrategy",
    external: true,
  },
  {
    title: "SmartExecution",
    desc: "Execute operations efficiently with RF, AI assistance, and real-time control.",
    href: "/smartexecution",
    external: true,
  },
  {
    title: "SmartAcceleration",
    desc: "Accelerate delivery using prebuilt kits, templates, and proven approaches.",
    href: "/smartacceleration",
    external: true,
  },
  {
    title: "SmartWarehouse Tech",
    desc: "Enable next-gen capabilities like RFID, voice, automation, and drone inventory.",
    href: "/smartwarehouse-tech",
    external: true,
  },
  {
    title: "SmartIntegrations",
    desc: "Extend SAP EWM with inspection, shipping, and process intelligence solutions.",
    href: "/smartintegrations",
    external: true,
  },
];

const AUDIENCE = [
  {
    icon: Building2,
    title: "Greenfield",
    context: "Organizations moving to SAP",
    outcome: "Plan the right warehouse architecture from day one",
  },
  {
    icon: Building,
    title: "Brownfield",
    context: "Existing SAP WM / EWM customers",
    outcome: "Fix inefficiencies without disrupting operations",
  },
  {
    icon: AlertTriangle,
    title: "Issue-driven",
    context: "Companies facing warehouse challenges",
    outcome: "Identify root causes and deliver quick wins",
    highlight: true,
  },
  {
    icon: Target,
    title: "Product-fit",
    context: "Customers exploring targeted improvements",
    outcome: "Deploy focused solutions with measurable ROI",
  },
  {
    icon: Network,
    title: "Multi-site",
    context: "Enterprises scaling warehouse operations",
    outcome: "Standardize and scale across locations",
  },
];

// --- Page ------------------------------------------------------------------
export default function PlatformHome() {
  const navigate = useNavigate();
  const goEvaluate = () => navigate("/evaluate");
  const scrollToPlatform = () => {
    document.getElementById("platform")?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (typeof window !== "undefined") {
      window.history.replaceState(null, "", "#platform");
    }
  };
  const mailExpert = "mailto:hello@primes4.com?subject=WarehouseX%20%E2%80%94%20Talk%20to%20Expert";

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <SEO
        title="WarehouseX | AI-Powered Warehouse Transformation"
        description="WarehouseX is PrimeS4's SAP-aligned warehouse transformation platform — combining SAP EWM, RF, automation and PRIVA AI for intelligent operational execution."
        keywords={[
          "WarehouseX",
          "SAP EWM AI",
          "SAP warehouse transformation",
          "Warehouse AI platform",
          "Intelligent warehouse operations",
          "Warehouse automation",
          "RF picking",
          "PrimeS4 AI",
        ]}
        canonical="/warehousex"
        image="/images/image-13.webp"
        imageAlt="WarehouseX AI-powered warehouse transformation platform"
      />
      <Header />
      <main className="flex-1">
        {/* SECTION 1 — HERO (full-bleed banner, aligned with PRIVA / Platforms / Innovation visual system) */}
        <section
          data-testid="platform-hero"
          className="relative isolate overflow-hidden text-white"
          style={{
            background:
              "linear-gradient(135deg, #050B22 0%, #0A1330 35%, #0B1734 70%, #050B22 100%)",
          }}
        >
          {/* Full-bleed WarehouseX hero artwork */}
          <img
            src="/images/image-13.webp"
            alt="WarehouseX — AI-powered warehouse transformation platform with execution intelligence, operational KPIs, task orchestration, and AI insights dashboard"
            className="absolute inset-0 w-full h-full object-cover object-[78%_center] md:object-right select-none pointer-events-none"
            loading="eager"
            draggable="false"
            data-testid="platform-hero-image"
            aria-hidden="true"
          />
          {/* Left-side darkening wash so copy stays legible while the right
              dashboard side of the artwork reads cleanly. */}
          <div
            className="absolute inset-0 pointer-events-none hidden md:block"
            style={{
              background:
                "linear-gradient(90deg, rgba(5,11,34,0.92) 0%, rgba(5,11,34,0.75) 28%, rgba(11,23,52,0.35) 55%, rgba(11,23,52,0.10) 78%, rgba(11,23,52,0) 100%)",
            }}
          />
          {/* Stronger mobile-only overlay to reduce brightness behind the
              text area when the image focus shifts toward the robot/pallet. */}
          <div
            className="absolute inset-0 pointer-events-none md:hidden"
            style={{
              background:
                "linear-gradient(180deg, rgba(5,11,34,0.92) 0%, rgba(5,11,34,0.74) 50%, rgba(5,11,34,0.55) 100%)",
            }}
          />
          {/* Subtle enterprise grid */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.05]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)",
              backgroundSize: "56px 56px",
              maskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
              WebkitMaskImage: "radial-gradient(ellipse at center, black 55%, transparent 90%)",
            }}
          />
          {/* Accent glows */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute -top-40 -left-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-25"
              style={{ background: "radial-gradient(circle, #1E40AF 0%, transparent 65%)" }}
            />
            <div
              className="absolute top-1/3 -right-32 w-[560px] h-[560px] rounded-full blur-3xl opacity-20"
              style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 65%)" }}
            />
          </div>
          {/* Bottom hairline */}
          <div
            className="absolute inset-x-0 bottom-0 h-px pointer-events-none"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(94,184,216,0.45) 50%, transparent 100%)",
            }}
          />

          <div
            className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 lg:pt-32 pb-16 md:pb-20 lg:pb-24 min-h-[88vh] md:min-h-[78vh] lg:min-h-[90vh] flex flex-col justify-start md:justify-center"
          >
            <div className="max-w-2xl" data-testid="platform-hero-copy">
              <p className="text-[11px] font-semibold tracking-[0.28em] uppercase text-[#5EB8D8] mb-6 flex items-center gap-3">
                <span className="inline-block h-px w-8 bg-[#5EB8D8]" />
                WarehouseX Platform
              </p>
              <h1
                data-testid="platform-hero-title"
                className="text-[34px] sm:text-5xl lg:text-[52px] xl:text-[56px] font-bold tracking-tight leading-[1.05]"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                <span>AI-Powered Warehouse </span>
                <span
                  className="bg-clip-text text-transparent"
                  style={{ backgroundImage: "linear-gradient(90deg, #5EB8D8 0%, #A78BFA 100%)" }}
                >
                  Transformation for SAP
                </span>
              </h1>
              <p
                className="mt-8 text-base sm:text-lg text-white/85 leading-relaxed max-w-xl"
                data-testid="platform-hero-sub"
              >
                Intelligent warehouse operations, execution visibility, operational insights, and transformation acceleration built for modern SAP-driven supply chains.
              </p>
              <p
                data-testid="hero-supporting-line"
                className="mt-4 text-sm sm:text-[15px] text-white/65 leading-relaxed max-w-xl"
              >
                From assessment and execution to operational intelligence and AI-enabled optimization — WarehouseX helps enterprises transform warehouse operations with measurable business outcomes.
              </p>
              <div
                className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 [&>a]:w-full [&>button]:w-full sm:[&>a]:w-auto sm:[&>button]:w-auto"
                data-testid="platform-hero-ctas"
              >
                <button
                  type="button"
                  data-testid="hero-cta-explore"
                  onClick={scrollToPlatform}
                  className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide text-white transition-colors"
                  style={{ backgroundColor: "#2563EB" }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#1D4ED8")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#2563EB")}
                >
                  Explore WarehouseX Platform
                  <ArrowRight
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2}
                  />
                </button>
                <button
                  type="button"
                  data-testid="hero-cta-request-demo"
                  onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
                  className="group inline-flex items-center justify-center gap-2 rounded-sm h-11 px-6 text-xs font-semibold tracking-wide border border-white/30 bg-white/5 text-white hover:bg-white/10 hover:border-white/60 transition-colors backdrop-blur-sm"
                >
                  Request Demo
                  <ArrowRight
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — OVERVIEW: The Challenge (Problem → Tension → Solution Hook) */}
        <section data-testid="section-why-warehousex" id="overview" className="py-20 md:py-28 scroll-mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
              {/* RIGHT: Large dominant image (source-first for mobile) */}
              <div className="lg:col-span-7 lg:order-2">
                <div className="relative rounded-sm overflow-hidden border border-[#E5E7EB] shadow-xl h-full min-h-[400px]">
                  <img
                    src="/images/warehouse-issues.webp"
                    alt="Frustrated warehouse supervisor buried in spreadsheets, paper inventory reports, and manual notes — operations running but complexity visible"
                    data-testid="challenge-image"
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#F59E0B]/10 via-transparent to-transparent pointer-events-none" />
                </div>
              </div>

              {/* LEFT: Content — Problem → Tension → Solution Hook */}
              <div className="lg:col-span-5 lg:order-1 flex flex-col justify-center">
                <Eyebrow>The Challenge</Eyebrow>
                <H2>Most Warehouses Are Running — But Underperforming</H2>
                <p
                  data-testid="challenge-subtext"
                  className="mt-5 text-base md:text-lg text-[#4B5563] leading-relaxed"
                >
                  Operations continue to run — but performance signals across throughput,
                  accuracy, and cost remain buried in disconnected systems, manual processes, and
                  reactive workflows.
                </p>

                {/* Two grouped mini sections — condensed (2 bullets each) */}
                <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div data-testid="problem-group-operational">
                    <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979] mb-3">
                      Operational Inefficiencies
                    </p>
                    <ul className="space-y-2 text-sm text-[#374151] leading-relaxed">
                      {[
                        "Manual processes limit productivity",
                        "Poor RF experience slows execution",
                      ].map((t) => (
                        <li key={t} className="flex gap-2.5">
                          <span className="mt-[8px] w-1 h-1 rounded-full bg-[#025979] shrink-0" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div data-testid="problem-group-visibility">
                    <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979] mb-3">
                      Visibility & Control Gaps
                    </p>
                    <ul className="space-y-2 text-sm text-[#374151] leading-relaxed">
                      {[
                        "Limited real-time visibility across operations",
                        "Issues persist without clear root cause",
                      ].map((t) => (
                        <li key={t} className="flex gap-2.5">
                          <span className="mt-[8px] w-1 h-1 rounded-full bg-[#025979] shrink-0" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Tension statement (business impact) */}
                <p
                  data-testid="challenge-tension"
                  className="mt-7 text-[15px] text-[#4B5563] leading-relaxed border-l-2 border-[#F59E0B] pl-4 italic"
                >
                  And as operations scale, these inefficiencies compound — driving higher costs,
                  inconsistent service levels, and reduced operational control.
                </p>

                {/* Solution hook */}
                <p
                  data-testid="challenge-hook"
                  className="mt-5 text-base font-semibold text-[#111827] leading-relaxed"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  WarehouseX transforms this complexity into clarity — converting fragmented
                  signals into structured, actionable transformation decisions.
                </p>

                {/* Subtle CTA */}
                <a
                  href="#platform"
                  data-testid="challenge-cta"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("platform")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    if (window.history?.replaceState) window.history.replaceState(null, "", "#platform");
                  }}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#025979] hover:text-[#03738C] group self-start"
                >
                  See How WarehouseX Solves This
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3 — INTRODUCING WAREHOUSEX (split) */}
        <section data-testid="section-what" className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              {/* Text column — source order first so on mobile it renders above the image;
                  on desktop its col-span-5 puts it on the left. */}
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3 mb-4 flex-nowrap">
                  <h2
                    className="text-3xl sm:text-4xl font-bold tracking-tight text-[#111827] leading-tight"
                    style={{ fontFamily: "Chivo, sans-serif" }}
                  >
                    Introducing
                  </h2>
                  <img loading="lazy"
                    src="/images/warehousex-t-800-x-200-px.svg"
                    alt="WarehouseX"
                    data-testid="introducing-warehousex-logo"
                    width="800"
                    height="200"
                    className="h-10 sm:h-11 w-auto shrink-0"
                  />
                </div>
                <Lead>
                  WarehouseX turns warehouse complexity into clear, actionable decisions —
                  combining diagnostics, execution, and optimization into a single platform.
                </Lead>
                <ul className="mt-8 space-y-4">
                  {[
                    "Evaluate warehouse maturity",
                    "Identify gaps and ROI opportunities",
                    "Deliver execution-ready roadmap",
                    "Accelerate transformation with built-in tools",
                  ].map((t, i) => (
                    <li key={i} data-testid={`what-bullet-${i}`} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-sm bg-[#025979] flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </span>
                      <span className="text-base text-[#111827] font-medium">{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Image — source order second; on mobile it stacks below the bullets,
                  on desktop its col-span-7 puts it on the right. */}
              <div className="lg:col-span-7">
                <img
                  src="/images/warehousex-intelligent.webp"
                  alt="WarehouseX — The Future Warehouse Is Intelligent: AI-Powered SAP Warehouse Transformation"
                  data-testid="introducing-dashboard-image"
                  className="w-full h-auto rounded-sm border border-[#E5E7EB] shadow-md"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4 — WHO IT'S FOR (moved up, right after "What is WarehouseX") */}
        <section id="who-its-for" data-testid="section-audience" className="py-20 md:py-28 scroll-mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-14">
              <Eyebrow>Who It's For</Eyebrow>
              <H2>Who WarehouseX is Designed For</H2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 lg:gap-8">
              {AUDIENCE.map((a, i) => {
                const Icon = a.icon;
                const highlighted = a.highlight === true;
                return (
                  <div
                    key={i}
                    data-testid={`audience-${i}`}
                    data-highlight={highlighted ? "true" : "false"}
                    className={`group relative bg-white rounded-sm p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg ${
                      highlighted
                        ? "border border-[#025979]/50 shadow-md"
                        : "border border-[#EEF2F5] hover:border-[#CBD5E1]"
                    }`}
                  >
                    {highlighted && (
                      <span
                        data-testid="audience-primary-badge"
                        className="absolute -top-2.5 left-6 inline-flex items-center gap-1 bg-[#025979] text-white text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-sm"
                      >
                        Most common
                      </span>
                    )}
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center mb-5 ${
                      highlighted ? "bg-[#025979]" : "bg-[#F0F9FF]"
                    }`}>
                      <Icon
                        className={`w-4 h-4 ${highlighted ? "text-white" : "text-[#025979]"}`}
                        strokeWidth={1.75}
                      />
                    </div>
                    <h3
                      className="text-base font-bold text-[#111827]"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                    >
                      {a.title}
                    </h3>
                    <p className="mt-1.5 text-xs text-[#6B7280] leading-relaxed">
                      {a.context}
                    </p>
                    <div className="mt-4 pt-4 border-t border-[#F3F4F6] flex items-start gap-2 text-xs text-[#025979] font-medium leading-relaxed">
                      <ArrowRight className="w-3.5 h-3.5 mt-[2px] shrink-0" strokeWidth={2.25} />
                      <span>{a.outcome}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SECTION 4 — SYSTEM OVERVIEW (5 pillar cards) */}
        <section
          id="warehousex-system"
          data-testid="section-system"
          className="py-20 md:py-28 scroll-mt-16"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-14">
              <Eyebrow>The System</Eyebrow>
              <H2>Built as a Complete Warehouse Transformation System</H2>
              <Lead>
                WarehouseX is not a collection of tools — it is a structured system that combines
                strategy, execution, acceleration, and innovation into one unified platform for
                warehouse transformation.
              </Lead>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-6">
              {SYSTEM_PILLARS.map((p, i) => {
                const content = (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-[10px] font-semibold tracking-[0.18em] text-[#025979] uppercase">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="w-8 h-px bg-[#025979]/30" />
                    </div>
                    <h3
                      className="text-base font-bold text-[#111827] leading-snug"
                      style={{ fontFamily: "Chivo, sans-serif" }}
                    >
                      {p.title}
                    </h3>
                    <p className="mt-2 text-[13px] text-[#4B5563] leading-relaxed">
                      {p.desc}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1 text-[11px] font-semibold text-[#025979] opacity-0 group-hover:opacity-100 transition-opacity">
                      Explore
                      <ArrowRight className="w-3 h-3" />
                    </span>
                  </>
                );

                const baseCls =
                  "group relative bg-white border border-[#E5E7EB] rounded-sm p-6 hover:border-[#025979]/40 hover:shadow-sm transition-all h-full";

                if (p.external) {
                  return (
                    <a
                      key={p.title}
                      href={p.href}
                      data-testid={`system-pillar-${i}`}
                      className={baseCls}
                    >
                      {content}
                    </a>
                  );
                }
                return (
                  <a
                    key={p.title}
                    href={p.href}
                    data-testid={`system-pillar-${i}`}
                    className={baseCls}
                    onClick={(e) => {
                      e.preventDefault();
                      const id = p.href.replace("#", "");
                      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                  >
                    {content}
                  </a>
                );
              })}
            </div>

            <div className="mt-14 flex justify-center">
              <button
                type="button"
                data-testid="system-cta-explore"
                onClick={() => {
                  document
                    .getElementById("platform")
                    ?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#025979] hover:text-[#03738C] group"
              >
                Explore the WarehouseX Platform
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
            </div>
          </div>
        </section>

        {/* SECTION 4b — WAREHOUSEX PLATFORM ECOSYSTEM (image insertion) */}
        <section
          id="ecosystem"
          data-testid="section-ecosystem"
          className="py-20 md:py-28 bg-white scroll-mt-16"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <Eyebrow>Ecosystem</Eyebrow>
              <H2>WarehouseX Platform Ecosystem</H2>
              <p className="mt-6 text-lg sm:text-xl text-[#374151] leading-relaxed font-medium">
                A unified SAP-native warehousing transformation ecosystem combining strategy, execution, automation, integrations, and intelligent operations.
              </p>
            </div>
            <div className="mt-12 lg:mt-16 max-w-6xl mx-auto">
              <img
                src="/images/image-20.webp"
                alt="WarehouseX Platform Ecosystem — 16 modular solutions across 5 intelligent pillars"
                data-testid="ecosystem-image"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* SECTION 5 — PLATFORM (alternating) */}
        <section id="platform" data-testid="section-platform" className="py-20 md:py-28 scroll-mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mb-20">
              <Eyebrow>The Platform</Eyebrow>
              <H2>WarehouseX Platform</H2>
              <p className="mt-6 text-lg sm:text-xl text-[#374151] leading-relaxed font-medium">
                A unified platform to evaluate, improve, and run your warehouse operations —
                powered by SAP expertise and AI.
              </p>
            </div>
            <div className="space-y-20 lg:space-y-28">
              {PLATFORM_BLOCKS.map((b, i) => (
                <PlatformBlock key={b.title} idx={i} reverse={i % 2 === 1} {...b} />
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5b — WAREHOUSEX USE CASE EXAMPLES (image insertion) */}
        <section
          id="use-cases"
          data-testid="section-use-cases"
          className="py-20 md:py-28 bg-[#F8FAFC] border-y border-[#E5E7EB] scroll-mt-16"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <Eyebrow>Use Cases</Eyebrow>
              <H2>WarehouseX Use Cases</H2>
              <p className="mt-6 text-lg sm:text-xl text-[#374151] leading-relaxed font-medium">
                Real-world SAP warehouse transformation scenarios powered through modular WarehouseX solutions.
              </p>
            </div>
            <div className="mt-12 lg:mt-16 max-w-6xl mx-auto">
              <img
                src="/images/warehousex-use-cases2.webp"
                alt="WarehouseX Use Case Examples — Warehouse assessments, inspection automation, RF modernization, AI-powered support, automation enablement, multi-site standardization, SAP innovation"
                data-testid="use-cases-image"
                className="w-full h-auto"
                loading="lazy"
              />
            </div>
          </div>
        </section>

        {/* SECTION 5 — HOW IT WORKS (horizontal timeline) */}
        <section id="how-it-works" data-testid="section-how" className="py-20 md:py-28 scroll-mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <Eyebrow>How It Works</Eyebrow>
              <H2>How WarehouseX Works</H2>
              <Lead>A structured, outcome-driven journey from assessment to continuous optimization.</Lead>
            </div>
            <div className="mt-14 relative">
              <div className="hidden lg:block absolute top-7 left-[8%] right-[8%] h-px bg-gradient-to-r from-transparent via-[#025979]/30 to-transparent" />
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 relative">
                {APPROACH.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} data-testid={`how-${i}`} className="text-left lg:text-center">
                      <div className="flex lg:justify-center mb-5">
                        <div className="relative w-14 h-14 bg-white border border-[#025979]/25 rounded-full flex items-center justify-center shadow-sm">
                          <Icon className="w-5 h-5 text-[#025979]" strokeWidth={1.75} />
                          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#025979] text-white text-[10px] font-bold flex items-center justify-center">
                            {i + 1}
                          </span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
                        {s.title}
                      </h3>
                      <p className="mt-1.5 text-sm text-[#6B7280] lg:max-w-[200px] lg:mx-auto">{s.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 — OUTCOMES (split, was Core Value) */}
        <section data-testid="section-value" className="py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className="lg:col-span-6">
                <CoreValueVisual />
              </div>
              <div className="lg:col-span-6">
                <Eyebrow>The Outcome</Eyebrow>
                <H2>What You Get with WarehouseX</H2>
                <Lead>
                  Every engagement delivers structured, execution-ready outputs your team can act on immediately.
                </Lead>
                <ul className="mt-8 space-y-4">
                  {[
                    "Comprehensive warehouse assessment across operations and systems",
                    "Clear identification of gaps, inefficiencies, and improvement opportunities",
                    "Prioritized transformation roadmap aligned to business impact",
                    "Execution-ready plan with defined phases, timelines, and ROI clarity",
                  ].map((t, i) => (
                    <li key={i} data-testid={`value-bullet-${i}`} className="flex items-start gap-3">
                      <span className="w-6 h-6 rounded-sm bg-[#025979] flex items-center justify-center shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                      </span>
                      <span className="text-base text-[#111827] font-medium">{t}</span>
                    </li>
                  ))}
                </ul>
                <p
                  data-testid="value-closing"
                  className="mt-8 text-base text-[#374151] italic leading-relaxed border-l-2 border-[#025979] pl-4"
                >
                  From insight to execution — everything you need to drive measurable warehouse transformation.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 10 — FINAL CTA (dark, focused, high hierarchy) */}
        <section id="evaluation" data-testid="section-final-cta" className="relative overflow-hidden bg-gradient-to-br from-[#03435C] via-[#0B2838] to-[#0B2A3A] scroll-mt-16">
          {/* subtle radial glow behind buttons */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/3 w-[640px] h-[640px] rounded-full bg-[#025979]/25 blur-3xl pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#5EB8D8]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 relative">
            <div className="text-center">
              <h2
                className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-white tracking-tight leading-[1.1]"
                style={{ fontFamily: "Chivo, sans-serif" }}
              >
                Ready to Transform Your<br />Warehouse Operations?
              </h2>
              <p className="mt-5 text-base sm:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed">
                Move from fragmented systems to a structured, execution-ready warehouse
                transformation — powered by WarehouseX.
              </p>
              <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
                <Button
                  data-testid="final-cta-evaluate"
                  onClick={goEvaluate}
                  className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm h-12 px-7 text-sm font-bold shadow-[0_10px_30px_-6px_rgba(2,89,121,0.7)] hover:shadow-[0_14px_36px_-6px_rgba(3,115,140,0.85)] hover:-translate-y-0.5 transition-all duration-200"
                >
                  Start Your Warehouse Evaluation
                </Button>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("requestDemo:open"))}
                  data-testid="final-cta-expert"
                  className="inline-flex items-center gap-2 border border-[#5EB8D8]/60 hover:border-[#5EB8D8] text-white rounded-sm h-12 px-7 text-sm font-semibold transition-all duration-200 hover:bg-[#5EB8D8]/10 group"
                >
                  Request Demo
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </button>
              </div>
              <p
                data-testid="final-cta-trust"
                className="mt-6 text-xs text-white/55 tracking-wide"
              >
                No commitment. Clear ROI. Actionable insights from day one.
              </p>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
